import React, { useState } from 'react';

import { candidateConditionTrait } from '../insights/candidateCondition';
import { TraitText } from '../trees/NodeTraits';
import {
  SplitCardCandidate,
  SplitCardData,
  countText,
  coverageText,
  liftText,
} from './splitCardData';
import './splitCard.css';

/**
 * One applied split, drawn from the write's own receipt.
 *
 * Folded by default past the first: a build that lands eleven splits stays readable
 * because each one is a row rather than a paragraph. Open, it answers the two questions
 * a reader actually has, which the old prose answered inconsistently or not at all:
 * what else was on the menu, and why this one.
 *
 * Nothing here is composed from free text. The condition goes through
 * `candidateConditionTrait`, the same phrasing a tree node's own condition gets.
 */
export function SplitCard({
  card,
  index,
  defaultOpen,
  onFocus,
}: {
  card: SplitCardData;
  /** Position in this build, 1-based. The receipt does not carry it; the panel does. */
  index?: number;
  defaultOpen?: boolean;
  onFocus: (targetType: string, targetId: string) => void;
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const applied = card.applied;
  const trait = candidateConditionTrait(applied);
  const lift = liftText(applied.lift);
  const coverage = coverageText(applied.coverage_pct);
  const beat = card.beat ?? [];
  const leaves = card.new_leaves ?? [];
  // At the root the share is of the whole model; anywhere else it is of this branch, and
  // calling both "of leads" is how a 26% and a 25.5% get read as the same population.
  const of = card.node === 1 ? 'of leads' : 'of branch';
  const below = Object.values(card.never_reached_the_menu ?? {}).reduce(
    (total, n) => total + (typeof n === 'number' ? n : 0),
    0
  );
  const where = card.node === 1 ? 'Root' : `node ${card.node}`;
  // Name what opening it gives you. A bare disclosure triangle says only "this
  // opens", which is the least useful half: the reader cannot tell whether it is
  // worth the click. A card with nothing behind it renders no row and does not
  // pretend to be interactive.
  const behind = [
    card.because ? 'Why this cut' : '',
    beat.length ? `the ${beat.length} it beat` : '',
    // The footer counts. It is rendered on `open` whenever the write created leaves, so
    // a receipt with leaves but no reason and nothing ranked used to produce a card that
    // advertised nothing, drew no chevron, and still expanded a row of node buttons when
    // you pressed Enter on it. Either it says what it holds or it is not a control.
    !card.because && !beat.length && leaves.length ? 'Where it split to' : '',
  ]
    .filter(Boolean)
    .join(', and ');
  const size = countText(card.population);

  return (
    <section
      className={`msc${open ? ' msc--open' : ''}${behind ? '' : ' msc--flat'}`}
    >
      {/* Not a button when there is nothing behind it. `msc--flat` took away the
          cursor and the hover, which is mouse-only dressing: it stayed in the tab
          order, kept its focus ring and still toggled on Enter. */}
      <Header
        interactive={Boolean(behind)}
        open={open}
        onToggle={() => setOpen((was) => !was)}
      >
        <span className="msc-slot">
          <span className="msc-where">
            {index ? `Split ${index} · ` : ''}
            {where}
            {size ? ` · ${size} leads` : ''}
          </span>
          <span className="msc-cut">
            {trait ? <TraitText trait={trait} /> : applied.label}
          </span>
        </span>
        <span className="msc-nums">
          {lift && (
            <span className="msc-num">
              <b>{lift}</b>
              <span>lift</span>
            </span>
          )}
          {coverage && (
            <span className="msc-num msc-num--dim">
              <b>{coverage}</b>
              <span>{of}</span>
            </span>
          )}
        </span>
        {behind && (
          <span className="msc-more">
            <span>{open ? 'Hide' : behind}</span>
            <span className="msc-chev" aria-hidden="true" />
          </span>
        )}
      </Header>

      {open && (
        <div className="msc-body">
          {card.because && (
            <p className="msc-why">
              <b>Why this one.</b> {card.because}
            </p>
          )}
          {beat.length > 0 && (
            <div className="msc-scroll">
              <table className="msc-table">
                <thead>
                  <tr>
                    <th>Considered at this node</th>
                    <th className="msc-r">Lift</th>
                    <th className="msc-r">Coverage</th>
                    <th className="msc-r">Leads</th>
                  </tr>
                </thead>
                <tbody>
                  <Row candidate={applied} picked />
                  {beat.map((other, i) => (
                    <Row
                      key={`${other.column ?? 'c'}-${i}`}
                      candidate={other}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {below > 0 && (
            <p className="msc-note">
              {below} more ranked below the cut
              {describeBelow(card.never_reached_the_menu)}.
            </p>
          )}
        </div>
      )}

      {open && (leaves.length > 0 || card.next?.node != null) && (
        <div className="msc-foot">
          {leaves.map((leaf) => (
            <button
              key={leaf.node}
              type="button"
              className="msc-leaf"
              onClick={() => onFocus('tree_node', `${card.tree}:${leaf.node}`)}
            >
              node {leaf.node}
              {leaf.side ? <em> · {leaf.side}</em> : null}
              {countText(leaf.population)
                ? ` ${countText(leaf.population)}`
                : ''}
            </button>
          ))}
          {card.next?.node != null && (
            <span className="msc-next">
              next: <b>node {card.next.node}</b>
            </span>
          )}
        </div>
      )}
    </section>
  );
}

/** The card's head: a real button where opening it gives you something, a plain box
 *  where it does not. Rendering a button either way put a control in the tab order that
 *  announced itself as expandable and had nothing to expand. */
function Header({
  interactive,
  open,
  onToggle,
  children,
}: {
  interactive: boolean;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  if (!interactive) return <div className="msc-hd">{children}</div>;
  return (
    <button
      type="button"
      className="msc-hd"
      aria-expanded={open}
      onClick={onToggle}
    >
      {children}
    </button>
  );
}

function Row({
  candidate,
  picked,
}: {
  candidate: SplitCardCandidate;
  picked?: boolean;
}) {
  const trait = candidateConditionTrait(candidate);
  return (
    <tr className={picked ? 'msc-picked' : undefined}>
      <td>
        {trait ? <TraitText trait={trait} /> : candidate.label}
        <span className="msc-dim">
          {picked ? 'chosen' : candidate.dimension || ''}
        </span>
      </td>
      <td className="msc-r">{liftText(candidate.lift) ?? '—'}</td>
      <td className="msc-r">{coverageText(candidate.coverage_pct) ?? '—'}</td>
      <td className="msc-r">{countText(candidate.true_population) ?? '—'}</td>
    </tr>
  );
}

/** " (technographic 9, firmographic 3)", or nothing when there is no breakdown. */
function describeBelow(byDimension?: Record<string, number>): string {
  const entries = Object.entries(byDimension ?? {}).filter(
    ([, n]) => typeof n === 'number' && n > 0
  );
  if (entries.length === 0) return '';
  return ` (${entries.map(([name, n]) => `${name} ${n}`).join(', ')})`;
}
